import { fileRecordKeys, useDeleteFileRecord } from "@/api/fileRecords";
import { RailReveal } from "@/components/rail-kit/RailReveal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { classifyTransfer } from "@/dnd/classifyTransfer";
import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DraggableData,
  type DroppableData,
} from "@/dnd/ids";
import { buildCopyPayloadFromRecord } from "@/dnd/payloads";
import { useAppNavigate } from "@/hooks/pane-routing";
import { RailRowMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import {
  DestinationDropdownMenuSub,
  isCanvasIntrinsicRecord,
} from "@/object-actions";
import { useAddToWorkspaceDestinationSubmenu } from "@/object-actions/transfer/useAddToWorkspaceSubmenu";
import { emitCanvasExpandNode } from "@/pages/canvas/constants/canvasExpandNode.constants";
import { useOpenCanvasLink } from "@/pages/canvas/hooks/useOpenCanvasLink";
import { canvasLinkTargetId } from "@/pages/canvas/utils/canvasLinkEntity";
import { isNodeLocked } from "@/pages/canvas/utils/nodeLock";
import { openNodeInDetailPane } from "@/pages/canvas/utils/openNodeInDetailPane";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import { IntegrationIcon } from "@cohesive/ui-components";
import { useDndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import type { Node } from "@xyflow/react";
import {
  Box,
  ChevronRight,
  Crosshair,
  Eye,
  EyeOff,
  File as FileIcon,
  Frame,
  Globe,
  LibraryBig,
  MoreHorizontal,
  PanelLeftOpen,
  Square,
  SquareDashedMousePointer,
  StickyNote,
  Trash2,
  Type,
  Wand2,
} from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  categorizeNode,
  getEntityPlatform,
  getNodeTitle,
  leafFileName,
  type ContentGroup,
  type ContentItem,
} from "./canvasRailContentsModel";
import { CanvasFileRowTrailing } from "./rows/CanvasFileRowTrailing";
import { ExplorerFileRow } from "./rows/ExplorerFileRow";
import { ExplorerRow } from "./rows/ExplorerRow";
import { SectionEmptyState } from "./rows/SectionHeader";
import type { useCanvasRailContents } from "./useCanvasRailContents";
import { useCanvasRailSelection } from "./useCanvasRailSelection";

export function CanvasRailContents({
  canvasId,
  canEdit,
  contents,
  selectedNodeIds,
  onSelectionChange,
  selectedFileIds,
  onFileSelectionChange,
}: {
  canvasId: string;
  canEdit: boolean;
  contents: ReturnType<typeof useCanvasRailContents>;
  selectedNodeIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  selectedFileIds: Set<string>;
  onFileSelectionChange: (ids: Set<string>) => void;
}) {
  const appNavigate = useAppNavigate();
  const openCanvasLink = useOpenCanvasLink();

  const {
    searchQuery,
    fileRecords,
    fileRecordIds,
    nodesById,
    fileRecordIdToNodeId,
    rootTypeGroups,
    sectionView,
    sectionsHidden,
    headerCount,
    hasBody,
    visibleItemIdOrder,
  } = contents;

  const { selectedItemIds, handleActivateItem, selectSingleItem } =
    useCanvasRailSelection({
      selectedNodeIds,
      onSelectionChange,
      selectedFileIds,
      onFileSelectionChange,
      fileRecordIds,
      fileRecordIdToNodeId,
      visibleItemIdOrder,
    });

  const openFileRecord = (record: FileRecord) => {
    // A block's only full-size surface is the lightbox on its node, and this
    // rail lists this canvas, so the node is known: expand it directly.
    // Everything else opens in the right pane (which itself redirects any
    // block that reaches it by id).
    const nodeId = fileRecordIdToNodeId.get(record.id);
    if (
      nodeId &&
      isCanvasIntrinsicRecord({
        record,
        nodeData: nodesById.get(nodeId)?.data,
      }) &&
      emitCanvasExpandNode(nodeId)
    ) {
      return;
    }
    appNavigate({
      pane: "rightPane",
      to: route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: record.id }),
    });
  };

  // Delete handler for the actual FileRecord (destructive). The shared
  // `useDeleteFileRecord` hook only optimistically updates the workspace
  // cache, so for canvas-pinned files we additionally invalidate the
  // canvas-scoped cache key on settle.
  const queryClient = useQueryClient();
  const deleteFileRecord = useDeleteFileRecord(undefined);
  const handleDeleteFile = (record: FileRecord) => {
    const name = leafFileName(record.path);
    const isReference = record.kind === "link";
    deleteFileRecord.mutate(record.id, {
      onSuccess: () => {
        toast.success(
          isReference
            ? "Removed from canvas — still in the workspace"
            : `Archived ${name}`,
          { duration: 2000 },
        );
      },
      onError: () =>
        toast.error(
          isReference
            ? "Failed to remove from canvas"
            : `Failed to archive ${name}`,
        ),
      onSettled: () => {
        if (canvasId) {
          queryClient.invalidateQueries({
            queryKey: fileRecordKeys.canvas(canvasId),
          });
          // The deleted file now belongs in the canvas recently-deleted
          // list; invalidate it so the recovery sheet doesn't serve a
          // stale cached list (global 10s staleTime) missing this file.
          queryClient.invalidateQueries({
            queryKey: fileRecordKeys.recentlyDeleted("canvas", canvasId),
          });
        }
      },
    });
  };

  // Non-destructive: removes the canvas nodes for the given ids. For
  // a file_record id the underlying FileRecord stays (the row appears
  // "Not placed"); for entity/object ids the node is removed outright.
  // Section nodes route through `canvas:delete-section` so their
  // children are preserved (mirrors the dedicated section menu).
  //
  // When this is called as part of a destructive file delete, callers
  // pass an `origin` starting with "no-undo" so the resulting Loro
  // commit is excluded from the undo stack — undoing a node removal
  // whose FileRecord no longer exists would resurrect a broken
  // reference. (Section delete is its own transaction and currently
  // ignores `origin`; today no caller pairs a section delete with an
  // irreversible side effect.)
  const handleRemoveFromCanvas = (itemIds: string[], origin?: string) => {
    for (const id of itemIds) {
      const node = nodesById.get(id);
      if (node?.type === "section") {
        window.dispatchEvent(
          new CustomEvent("canvas:delete-section", {
            detail: { sectionId: id },
          }),
        );
      } else {
        window.dispatchEvent(
          new CustomEvent("canvas:remove-node", {
            detail: { nodeId: id, origin },
          }),
        );
      }
    }
  };

  // ─── Unified row context menu (files + entities + objects) ─────
  // One menu state covers every selectable row. Section folder rows
  // keep their own dedicated menu (Locate + Delete section) because
  // their actions are semantically different and they're not part of
  // the unified selection space.
  const [itemMenu, setItemMenu] = useState<{
    x: number;
    y: number;
    targetIds: string[];
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    targets: FileRecord[];
    /** Non-file nodes deleted alongside the files (mixed selections). */
    nodeIds?: string[];
  } | null>(null);
  const [sectionMenu, setSectionMenu] = useState<{
    x: number;
    y: number;
    section: Node;
  } | null>(null);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState<Node | null>(
    null,
  );

  const handleSectionContextMenu = (e: React.MouseEvent, section: Node) => {
    e.preventDefault();
    e.stopPropagation();
    setSectionMenu({ x: e.clientX, y: e.clientY, section });
  };

  const handleLocateSection = (section: Node) => {
    setSectionMenu(null);
    window.dispatchEvent(
      new CustomEvent("canvas:focus-node", {
        detail: { nodeId: section.id, keepZoom: true },
      }),
    );
  };

  const handleDeleteSection = (section: Node) => {
    window.dispatchEvent(
      new CustomEvent("canvas:delete-section", {
        detail: { sectionId: section.id },
      }),
    );
  };

  // Right-click / kebab on any selectable row routes here. Mirrors
  // OS file-manager behavior: right-clicking an unselected row scopes
  // the action to that row alone; right-clicking a row that's part of
  // the multi-selection acts on the whole set.
  const openItemMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;
    const inSelection = selectedItemIds.has(id);
    let targetIds: string[];
    if (inSelection && selectedItemIds.size > 1) {
      targetIds = Array.from(selectedItemIds);
    } else {
      targetIds = [id];
      if (!inSelection) selectSingleItem(id);
    }
    setItemMenu({ x: e.clientX, y: e.clientY, targetIds });
  };

  const handleLocateItem = (itemId: string) => {
    setItemMenu(null);
    window.dispatchEvent(
      new CustomEvent("canvas:focus-node", {
        detail: { nodeId: itemId, keepZoom: true },
      }),
    );
  };

  // Hide/show nodes on the surface (sets `node.hidden`) — the non-destructive
  // counterpart to delete. Hidden nodes stay in this list with a "Show on
  // canvas" affordance.
  const handleSetNodesHidden = (nodeIds: string[], hidden: boolean) => {
    for (const nodeId of nodeIds) {
      window.dispatchEvent(
        new CustomEvent("canvas:set-node-hidden", {
          detail: { nodeId, hidden },
        }),
      );
    }
  };

  // Performs the actual delete for a set of file targets. Deleted file
  // records land in the recently-deleted list and can be recovered, so
  // unpublished deletes run straight from the context menu with no
  // confirmation; only published files still funnel through the dialog
  // (see the menu onClick below) because deleting them irreversibly
  // breaks their public link.
  const performDeleteFiles = (targets: FileRecord[]) => {
    // Any placed targets get their node removed first so the canvas
    // never holds a dangling reference to a soon-to-be-deleted record.
    // The paired node removal is committed with a "no-undo" origin and
    // excluded from the undo stack.
    const placedNodeIds = targets
      .map((r) => fileRecordIdToNodeId.get(r.id))
      .filter((id): id is string => id !== undefined);
    if (placedNodeIds.length > 0)
      handleRemoveFromCanvas(placedNodeIds, "no-undo:file-delete");
    targets.forEach((record) => handleDeleteFile(record));
    onFileSelectionChange(new Set());
  };

  // Delete for a mixed selection: file records go through the trash path
  // (which also removes their nodes); plain nodes are removed outright —
  // that IS their delete, there's no record behind them.
  const performDeleteSelection = (
    fileTargets: FileRecord[],
    nonFileNodeIds: string[],
  ) => {
    if (nonFileNodeIds.length > 0) handleRemoveFromCanvas(nonFileNodeIds);
    if (fileTargets.length > 0) performDeleteFiles(fileTargets);
  };

  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    const { targets, nodeIds } = confirmDelete;
    setConfirmDelete(null);
    performDeleteSelection(targets, nodeIds ?? []);
  };

  // ─── Capability model for the unified menu ──────────────────────
  // The available actions are derived from what's actually in the
  // selection. Mixed selections (files + non-file nodes) hide the
  // "Delete file" option entirely so the user has to isolate files
  // before performing the irreversible operation — this avoids the
  // "did I just delete the wrong thing?" footgun that scoped-subset
  // deletes would create.
  const itemMenuCapabilities = useMemo(() => {
    if (!itemMenu) return null;
    const fileTargets: FileRecord[] = [];
    const nonFileNodeIds: string[] = [];
    const placedItemIds: string[] = [];
    // Hide/Show split: a node is either visible (can be hidden unless
    // locked) or already hidden (can be shown). Hide is the non-destructive counterpart to
    // delete — the node and the file's membership stay intact.
    const hideableNodeIds: string[] = [];
    const hiddenNodeIds: string[] = [];
    let unplacedFileCount = 0;
    for (const id of itemMenu.targetIds) {
      const record = fileRecords.find((r) => r.id === id);
      if (record) {
        fileTargets.push(record);
        const nodeId = fileRecordIdToNodeId.get(id);
        if (nodeId) {
          placedItemIds.push(nodeId);
          const node = nodesById.get(nodeId);
          if (node?.hidden === true) hiddenNodeIds.push(nodeId);
          else if (!isNodeLocked(node)) hideableNodeIds.push(nodeId);
        } else unplacedFileCount += 1;
        continue;
      }
      const node = nodesById.get(id);
      if (node) {
        nonFileNodeIds.push(id);
        placedItemIds.push(id);
        if (node.hidden === true) hiddenNodeIds.push(id);
        else if (!isNodeLocked(node)) hideableNodeIds.push(id);
      }
    }
    const count = fileTargets.length + nonFileNodeIds.length;
    const hasFiles = fileTargets.length > 0;
    const hasNodes = nonFileNodeIds.length > 0;
    const hasPlaced = placedItemIds.length > 0;
    // Open is meaningful for a single file (always — opens the file
    // detail pane) or a single entity node (opens the entity detail
    // pane via the same router map `EntityNode.handleEntityClick`
    // uses). "Object" nodes (shapes / text / sections / widgets)
    // have no detail pane, so Open is suppressed for them.
    let openTarget:
      | { kind: "file"; record: FileRecord }
      | { kind: "node"; node: Node }
      | null = null;
    if (count === 1) {
      if (fileTargets.length === 1) {
        openTarget = { kind: "file", record: fileTargets[0] };
      } else if (nonFileNodeIds.length === 1) {
        const node = nodesById.get(nonFileNodeIds[0]);
        if (node && categorizeNode(node) === "entity") {
          openTarget = { kind: "node", node };
        }
      }
    }
    return {
      count,
      isSingle: count === 1,
      hasFiles,
      hasNodes,
      hasPlaced,
      hasUnplacedFiles: unplacedFileCount > 0,
      placedItemIds,
      hideableNodeIds,
      hiddenNodeIds,
      nonFileNodeIds,
      fileTargets,
      // Locate is only meaningful for a single placed target. It works
      // for a placed file (placedItemIds holds the node id) or for an
      // entity/object node. The dispatched value is always a node id.
      locateTargetId: count === 1 && hasPlaced ? placedItemIds[0] : null,
      openTarget,
    };
  }, [itemMenu, fileRecords, fileRecordIdToNodeId, nodesById]);

  // Add to workspace as the nested destination cascade (home-workspace-locked)
  // — same picker as every registry file menu. Only single pure-file
  // selections promote, matching the old dialog's gate — and never a block:
  // it is canvas-intrinsic (D4), which its node's display copy settles.
  const addToLibraryCandidate =
    itemMenuCapabilities?.isSingle &&
    itemMenuCapabilities.hasFiles &&
    !itemMenuCapabilities.hasNodes
      ? (itemMenuCapabilities.fileTargets[0] ?? null)
      : null;
  const addToLibraryTarget =
    addToLibraryCandidate &&
    !isCanvasIntrinsicRecord({
      record: addToLibraryCandidate,
      nodeData: nodesById.get(
        fileRecordIdToNodeId.get(addToLibraryCandidate.id) ?? "",
      )?.data,
    })
      ? addToLibraryCandidate
      : null;
  const addToLibrarySubmenu =
    useAddToWorkspaceDestinationSubmenu(addToLibraryTarget);

  // ─── Drag payloads for multi-select ─────────────────────────────
  // We thread `parentSectionId` through to the payload so the
  // classifier can short-circuit "already in this section" / "already
  // at root" without consulting live canvas state.
  const selectedRecordRows = useMemo(
    () =>
      fileRecords
        .filter((r) => selectedFileIds.has(r.id))
        .map((r) => {
          const nodeId = fileRecordIdToNodeId.get(r.id);
          return {
            record: r,
            scope: { canvasId },
            placed: nodeId !== undefined,
            parentSectionId: nodeId
              ? (nodesById.get(nodeId)?.parentId ?? null)
              : null,
          };
        }),
    [fileRecords, selectedFileIds, canvasId, fileRecordIdToNodeId, nodesById],
  );

  // ─── Body droppable ─────────────────────────────────────────────
  const bodyDroppableData: DroppableData | undefined = canvasId
    ? {
        surface: "canvas-scope",
        destination: destinationFor("canvas-scope", { canvasId }),
      }
    : undefined;
  const bodyDrop = useDroppable({
    id: canvasId ? droppableId.canvasScope(canvasId) : "canvas-scope:none",
    data: bodyDroppableData,
    disabled: !canvasId,
  });

  // Live classification for the body itself — when the user hovers
  // canvas root with a drag the classifier may resolve to noop /
  // invalid (e.g. an entity already at root, or a section dropped
  // back where it is). The body picks up an amber tint in those
  // cases for parity with section + folder rows.
  const { active: bodyActive } = useDndContext();
  const bodyLiveDraggable = bodyActive
    ? asDraggableData(bodyActive.data.current)
    : null;
  const bodyClassification =
    bodyDrop.isOver && bodyLiveDraggable && bodyDroppableData
      ? classifyTransfer(bodyLiveDraggable, bodyDroppableData.destination)
      : null;
  const bodyDropState: "idle" | "over" | "invalid" = !bodyDrop.isOver
    ? "idle"
    : bodyClassification?.kind === "noop" ||
        bodyClassification?.kind === "invalid"
      ? "invalid"
      : "over";

  if (!canvasId) return null;

  // ─── Render helpers shared by both view modes ───────────────────
  const renderFileRow = (item: ContentItem, dragNamespace: string) => {
    const record = item.fileRecord;
    if (!record) return null;
    const placed = item.placed;
    const nodeId = fileRecordIdToNodeId.get(record.id);
    return (
      <ExplorerFileRow
        key={item.id}
        record={record}
        scope={{ canvasId }}
        dragNamespace={dragNamespace}
        selected={selectedItemIds.has(record.id)}
        selectedRecords={selectedRecordRows}
        onClick={(e) =>
          handleActivateItem(record.id, e, () => openFileRecord(record))
        }
        onContextMenu={(e) => openItemMenu(e, record.id)}
        // Hidden nodes (and legacy node-less members) render dimmed — still
        // full members of the canvas, just not visible on the surface.
        dimmed={!placed || item.hidden}
        leadingTitle={
          !placed
            ? "Not placed on canvas"
            : item.hidden
              ? "Hidden on canvas"
              : undefined
        }
        sourcePlaced={placed}
        sourceParentSectionId={
          placed ? (item.parentSectionId ?? null) : undefined
        }
        trailing={
          <CanvasFileRowTrailing
            record={record}
            onOpenMenu={(e, r) => openItemMenu(e, r.id)}
          />
        }
        htmlProps={
          nodeId
            ? {
                onMouseEnter: () =>
                  window.dispatchEvent(
                    new CustomEvent("canvas:highlight-nodes", {
                      detail: { nodeIds: [nodeId] },
                    }),
                  ),
                onMouseLeave: () =>
                  window.dispatchEvent(
                    new CustomEvent("canvas:highlight-nodes", {
                      detail: { nodeIds: null },
                    }),
                  ),
              }
            : undefined
        }
      />
    );
  };

  const renderNodeRow = (
    item: ContentItem,
    dragNamespace: string,
    className?: string,
  ) => {
    const node = item.node;
    if (!node) return null;
    // Sections in type view (rendered via NodeRowAdapter inside the
    // "Sections" group) skip the unified row menu — the section menu
    // semantics live on SectionFolderRow in section view, and we don't
    // want a generic "Remove from canvas" path that would delete the
    // section without preserving its children.
    const supportsContextMenu = canEdit && node.type !== "section";
    return (
      <NodeRowAdapter
        key={item.id}
        node={node}
        canvasId={canvasId}
        canEdit={canEdit}
        dragNamespace={dragNamespace}
        selected={selectedItemIds.has(node.id)}
        onActivate={handleActivateItem}
        onOpen={() =>
          openNodeInDetailPane(node, appNavigate, canvasId, { openCanvasLink })
        }
        onContextMenu={
          supportsContextMenu ? (e, n) => openItemMenu(e, n.id) : undefined
        }
        // Hidden nodes stay listed, dimmed (matching hidden file rows).
        className={cn(className, item.hidden && "opacity-60")}
      />
    );
  };

  const renderItem = (
    item: ContentItem,
    opts: { dragNamespace: string; indented?: boolean },
  ) => {
    if (item.category === "file")
      return renderFileRow(item, opts.dragNamespace);
    return renderNodeRow(item, opts.dragNamespace);
  };

  const isSearching = searchQuery.length > 0;
  const emptyMessage = isSearching
    ? "No matching contents"
    : "No items on this canvas";
  const showSections = !sectionsHidden && sectionView.entries.length > 0;
  // Content exists but every folder is hidden — say so rather than reading
  // as an empty canvas (DS 021's drawer copy).
  const allFilteredOut =
    hasBody && !showSections && rootTypeGroups.length === 0;

  // One lens (DS 021): a Sections folder leads — each canvas section nests
  // a level deeper inside it, a real container rather than a type bucket —
  // and the unsectioned remainder reads as type folders, every folder
  // closed at rest with its count.
  const listBody =
    headerCount === 0 && !hasBody ? (
      <SectionEmptyState message={emptyMessage} />
    ) : allFilteredOut ? (
      <SectionEmptyState message="Everything is filtered out. Show a type to see it here." />
    ) : (
      <>
        {showSections && (
          <ContentsFolder
            icon={
              <SquareDashedMousePointer className="h-4 w-4 shrink-0 text-foreground/70" />
            }
            label="Sections"
            count={sectionView.entries.length}
            // Searching pre-opens the folder — a match hidden behind a
            // closed head reads as no match.
            defaultOpen={isSearching}
          >
            {sectionView.entries.map(({ section, items }) => (
              <SectionFolderRow
                key={section.id}
                section={section}
                items={items}
                canvasId={canvasId}
                renderItem={(it) =>
                  renderItem(it, {
                    dragNamespace: `contents:section:${section.id}`,
                    indented: true,
                  })
                }
                onContextMenu={canEdit ? handleSectionContextMenu : undefined}
              />
            ))}
          </ContentsFolder>
        )}
        {rootTypeGroups.map((group) => (
          <ContentGroupRow
            key={group.key}
            group={group}
            defaultOpen={isSearching}
            renderItem={(it) =>
              renderItem(it, { dragNamespace: `contents:type:${group.key}` })
            }
          />
        ))}
      </>
    );

  return (
    <div
      ref={bodyDrop.setNodeRef}
      className={cn(
        "relative py-1",
        bodyDropState === "over" &&
          "rounded-md bg-primary/[0.04] ring-1 ring-inset ring-rust/30",
        bodyDropState === "invalid" &&
          "rounded-md opacity-90 ring-1 ring-inset ring-rust/40",
      )}
    >
      {listBody}

      {itemMenu && itemMenuCapabilities && (
        <RailRowMenu
          kebab={false}
          label="Item"
          open
          onOpenChange={(o) => {
            if (!o) setItemMenu(null);
          }}
          point={{ x: itemMenu.x, y: itemMenu.y }}
          contentWidthClassName="w-52"
        >
          {/*
            Open: routes a single file to its FileRecord detail pane,
            or a single entity node to the same detail pane the
            EntityNode click opens. Object rows (shapes / text /
            sections / widgets) have no detail pane, so the entry is
            suppressed for them and `openTarget` is null. Multi-target
            menus skip Open entirely — the user has to isolate a row
            first to disambiguate intent.
          */}
          {itemMenuCapabilities.openTarget && (
            <DropdownMenuItem
              onClick={() => {
                const target = itemMenuCapabilities.openTarget!;
                if (target.kind === "file") {
                  openFileRecord(target.record);
                } else {
                  openNodeInDetailPane(target.node, appNavigate, canvasId, {
                    openCanvasLink,
                  });
                }
              }}
            >
              <PanelLeftOpen />
              Open
            </DropdownMenuItem>
          )}
          {/*
            Locate: only meaningful for a single target (multi-select
            "locate" doesn't have a single canvas position to focus).
            For unplaced files the entry stays visible but disabled
            with a "Not placed on canvas" label so the menu's row
            count is consistent regardless of placement state — same
            treatment as `ChatsScope`'s session menu. Works uniformly
            across files (placed FileRecords) and non-file nodes
            since both share the node-id space on the canvas.
          */}
          {itemMenuCapabilities.isSingle && (
            <DropdownMenuItem
              disabled={!itemMenuCapabilities.locateTargetId}
              onClick={() => {
                const id = itemMenuCapabilities.locateTargetId;
                if (!id) return;
                handleLocateItem(id);
              }}
            >
              <Crosshair />
              {itemMenuCapabilities.locateTargetId
                ? "Locate on canvas"
                : "Not placed on canvas"}
            </DropdownMenuItem>
          )}
          {/*
            Add to workspace (A3): promote a single canvas file — same-id move
            into the workspace tree, a reference left behind on the canvas.
            Single pure-file selections only; the folder-choosing dialog
            targets one record. References and workspace files are already
            there, so the entry is suppressed for them.
          */}
          {addToLibrarySubmenu && (
            <DestinationDropdownMenuSub
              submenu={addToLibrarySubmenu}
              label="Add to workspace"
              icon={<LibraryBig />}
            />
          )}
          {/* The tier marker: a workspace file (or a reference to one) states
              its home instead of offering promotion — disabled by design. */}
          {itemMenuCapabilities.isSingle &&
            itemMenuCapabilities.hasFiles &&
            !itemMenuCapabilities.hasNodes &&
            itemMenuCapabilities.fileTargets[0] &&
            (itemMenuCapabilities.fileTargets[0].kind === "link" ||
              itemMenuCapabilities.fileTargets[0].class === "workspace") && (
              <DropdownMenuItem disabled>
                <LibraryBig />
                In the workspace
              </DropdownMenuItem>
            )}
          {/*
            Hide on canvas: sets `hidden: true` on every visible node in
            the selection (file or non-file) — the non-destructive
            counterpart to Delete. The node (and, for files, the canvas
            membership) stays intact; the canvas just stops rendering it,
            and the row stays here with a "Show on canvas" affordance.
          */}
          {itemMenuCapabilities.hideableNodeIds.length > 0 && (
            <DropdownMenuItem
              onClick={() => {
                handleSetNodesHidden(
                  itemMenuCapabilities.hideableNodeIds,
                  true,
                );
              }}
            >
              <EyeOff />
              {itemMenuCapabilities.hideableNodeIds.length > 1
                ? `Hide ${itemMenuCapabilities.hideableNodeIds.length} on canvas`
                : "Hide on canvas"}
            </DropdownMenuItem>
          )}
          {itemMenuCapabilities.hiddenNodeIds.length > 0 && (
            <DropdownMenuItem
              onClick={() => {
                handleSetNodesHidden(itemMenuCapabilities.hiddenNodeIds, false);
              }}
            >
              <Eye />
              {itemMenuCapabilities.hiddenNodeIds.length > 1
                ? `Show ${itemMenuCapabilities.hiddenNodeIds.length} on canvas`
                : "Show on canvas"}
            </DropdownMenuItem>
          )}
          {/*
            Archive/Delete — available for every selection, file or not, so
            the rail's action set matches the canvas node menus. Files archive
            (restorable; their nodes removed alongside) so file-only selections
            say "Archive"; plain nodes are removed outright — that is their
            delete — so selections involving them say "Delete". Skip the
            confirm unless a published file is involved (archiving one
            permanently breaks its public link).
          */}
          {itemMenuCapabilities.count > 0 && (
            <DropdownMenuItem
              danger
              onClick={() => {
                const { fileTargets, nonFileNodeIds } = itemMenuCapabilities;
                if (fileTargets.some((t) => t.kind !== "link" && t.published)) {
                  setConfirmDelete({
                    targets: fileTargets,
                    nodeIds: nonFileNodeIds,
                  });
                } else {
                  performDeleteSelection(fileTargets, nonFileNodeIds);
                }
              }}
            >
              <Trash2 />
              {(() => {
                const allReferences =
                  itemMenuCapabilities.nonFileNodeIds.length === 0 &&
                  itemMenuCapabilities.fileTargets.length > 0 &&
                  itemMenuCapabilities.fileTargets.every(
                    (t) => t.kind === "link",
                  );
                const verb =
                  itemMenuCapabilities.nonFileNodeIds.length > 0
                    ? "Delete"
                    : allReferences
                      ? "Remove from canvas"
                      : "Archive";
                return itemMenuCapabilities.count > 1 && !allReferences
                  ? `${verb} ${itemMenuCapabilities.count} items`
                  : verb;
              })()}
            </DropdownMenuItem>
          )}
        </RailRowMenu>
      )}

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                if (!confirmDelete) return "";
                const fileCount = confirmDelete.targets.length;
                const nodeCount = confirmDelete.nodeIds?.length ?? 0;
                if (nodeCount > 0)
                  return `Delete ${fileCount + nodeCount} items?`;
                return fileCount > 1
                  ? `Archive ${fileCount} files?`
                  : `Archive "${leafFileName(confirmDelete.targets[0].path)}"?`;
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                if (!confirmDelete) return "";
                const fileCount = confirmDelete.targets.length;
                const nodeCount = confirmDelete.nodeIds?.length ?? 0;
                const filesPart =
                  fileCount > 1
                    ? `The ${fileCount} files will be archived — you can restore them from Archived.`
                    : "The file will be archived — you can restore it from Archived.";
                const nodesPart =
                  nodeCount > 0
                    ? ` ${nodeCount === 1 ? "1 other canvas item" : `${nodeCount} other canvas items`} in the selection will be removed permanently.`
                    : "";
                return filesPart + nodesPart;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {(() => {
            if (!confirmDelete) return null;
            const publishedCount = confirmDelete.targets.filter(
              (t) => t.published,
            ).length;
            if (publishedCount === 0) return null;
            const isSingle = confirmDelete.targets.length === 1;
            return (
              <div className="flex items-start gap-2.5 rounded-md border border-lc-warning/60 bg-gold/10 p-3 text-lc-warning dark:border-lc-warning/30 dark:bg-lc-warning/10 dark:text-lc-warning">
                <Globe className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="space-y-0.5 text-sm">
                  <div className="font-medium">
                    {isSingle
                      ? "This file is published"
                      : publishedCount === 1
                        ? "1 selected file is published"
                        : `${publishedCount} selected files are published`}
                  </div>
                  <div className="text-xs opacity-90">
                    Archiving will permanently break any public link pointing at
                    {isSingle ? " it." : " them."}
                  </div>
                </div>
              </div>
            );
          })()}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
            >
              {(() => {
                if (!confirmDelete) return "";
                const fileCount = confirmDelete.targets.length;
                const nodeCount = confirmDelete.nodeIds?.length ?? 0;
                if (nodeCount > 0)
                  return `Delete ${fileCount + nodeCount} items`;
                return fileCount > 1
                  ? `Archive ${fileCount} files`
                  : "Archive file";
              })()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {sectionMenu && (
        <RailRowMenu
          kebab={false}
          label="Section"
          open
          onOpenChange={(o) => {
            if (!o) setSectionMenu(null);
          }}
          point={{ x: sectionMenu.x, y: sectionMenu.y }}
        >
          <DropdownMenuItem
            onClick={() => handleLocateSection(sectionMenu.section)}
          >
            <Crosshair />
            Locate
          </DropdownMenuItem>
          <DropdownMenuItem
            danger
            onClick={() => setConfirmDeleteSection(sectionMenu.section)}
          >
            <Trash2 />
            Delete section
          </DropdownMenuItem>
        </RailRowMenu>
      )}

      <AlertDialog
        open={!!confirmDeleteSection}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteSection(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDeleteSection
                ? `Delete "${getNodeTitle(confirmDeleteSection)}"?`
                : ""}
            </AlertDialogTitle>
            <AlertDialogDescription>
              The section will be removed from the canvas. Items inside it stay
              on the canvas. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                if (confirmDeleteSection) {
                  handleDeleteSection(confirmDeleteSection);
                }
                setConfirmDeleteSection(null);
              }}
            >
              Delete section
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Type-view group row ──────────────────────────────────────────────

/** A closed-at-rest type folder: head with icon, label, disclosure, and the
 *  count a closed folder owes you (sans, per the DS register audit). */
function ContentsFolder({
  icon,
  label,
  count,
  defaultOpen = false,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="py-0.5">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        className="mx-2 flex min-h-7 cursor-pointer select-none items-center gap-2 rounded-md px-2 text-xs font-medium text-foreground/75 hover:bg-n-05 hover:text-foreground"
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((v) => !v);
          }
        }}
      >
        {icon}
        <span className="min-w-0 truncate">{label}</span>
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-150",
            isOpen && "rotate-90",
          )}
        />
        <span className="flex-1" />
        <span className="text-[11px] tabular-nums text-muted-foreground/60">
          {count}
        </span>
      </div>

      <RailReveal open={isOpen} contentClassName="ml-3 flex flex-col">
        {children}
      </RailReveal>
    </div>
  );
}

function ContentGroupRow({
  group,
  defaultOpen,
  renderItem,
}: {
  group: ContentGroup;
  defaultOpen?: boolean;
  renderItem: (item: ContentItem) => React.ReactNode;
}) {
  return (
    <ContentsFolder
      icon={<ContentGroupIcon group={group} />}
      label={group.label}
      count={group.items.length}
      defaultOpen={defaultOpen}
    >
      {group.items.map((it) => renderItem(it))}
    </ContentsFolder>
  );
}

// ─── Section-view folder row ──────────────────────────────────────────

function SectionFolderRow({
  section,
  items,
  canvasId,
  renderItem,
  onContextMenu,
}: {
  section: Node;
  items: ContentItem[];
  canvasId: string;
  renderItem: (item: ContentItem) => React.ReactNode;
  onContextMenu?: (e: React.MouseEvent, section: Node) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const title = getNodeTitle(section);

  // Section drag payload: only direct file children make the trip.
  // Sub-sections / shapes / text / entities / widgets are excluded —
  // they have no meaningful workspace representation, and surfacing
  // them in the payload would suggest a partial copy that can't
  // actually happen.
  const sectionFilePayloads = useMemo(
    () =>
      items
        .filter((it) => it.category === "file" && it.fileRecord)
        .map((it) =>
          buildCopyPayloadFromRecord(
            it.fileRecord!,
            { canvasId },
            { placed: it.placed },
          ),
        ),
    [items, canvasId],
  );
  const draggableData: DraggableData = useMemo(
    () => ({
      kind: "section",
      payload: {
        sectionId: section.id,
        sectionName: title,
        sourceCanvasId: canvasId,
        files: sectionFilePayloads,
      },
    }),
    [section.id, title, canvasId, sectionFilePayloads],
  );

  // Per-section unique drag id; matches our existing dragNamespace
  // pattern so dnd-kit's id registry stays clean.
  const draggable = useDraggable({
    id: `section-row:contents:section:${section.id}`,
    data: draggableData,
  });

  // The section's droppable surface covers the *entire visible section*
  // — the header row plus the children list (when open). Putting the
  // droppable on the outer wrapper means a user dragging an entity
  // anywhere over the section (header *or* the children area) targets
  // the section, not the canvas-scope body underneath. The header div
  // alone wouldn't intercept hovers inside the open content area.
  const droppableData: DroppableData = useMemo(
    () => ({
      surface: "canvas-section",
      destination: destinationFor("canvas-section", {
        canvasId,
        sectionId: section.id,
      }),
    }),
    [canvasId, section.id],
  );
  const droppable = useDroppable({
    id: droppableId.canvasSection(canvasId, section.id),
    data: droppableData,
  });

  // Live classification mirrors `ExplorerFolderRow` — the row lights
  // primary on a valid drop and amber on noop / invalid.
  const { active } = useDndContext();
  const liveDraggable = active ? asDraggableData(active.data.current) : null;
  const classification =
    droppable.isOver && liveDraggable
      ? classifyTransfer(liveDraggable, droppableData.destination)
      : null;
  const dropState: "idle" | "over" | "invalid" = !droppable.isOver
    ? "idle"
    : classification?.kind === "noop" || classification?.kind === "invalid"
      ? "invalid"
      : "over";

  return (
    <div
      ref={droppable.setNodeRef}
      className={cn(
        "py-0.5 mx-2 rounded-lg",
        dropState === "over" &&
          "bg-primary/[0.05] ring-1 ring-rust/40 dark:bg-primary/[0.10]",
        dropState === "invalid" && "ring-1 ring-rust/40 opacity-80",
      )}
    >
      <div
        ref={draggable.setNodeRef}
        {...draggable.attributes}
        {...draggable.listeners}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        className={cn(
          "flex min-h-7 cursor-pointer select-none items-center gap-2 rounded-md px-2 text-[13px] font-medium text-foreground/85 hover:bg-n-05 hover:text-foreground",
        )}
        onClick={() => setIsOpen((v) => !v)}
        onContextMenu={
          onContextMenu ? (e) => onContextMenu(e, section) : undefined
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((v) => !v);
          }
        }}
        style={
          draggable.isDragging
            ? { opacity: 0.4, cursor: "grabbing" }
            : undefined
        }
      >
        <SquareDashedMousePointer
          className="h-4 w-4 shrink-0 text-foreground/70"
          strokeWidth={2}
        />
        <span className="min-w-0 truncate">{title}</span>
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-150",
            isOpen && "rotate-90",
          )}
        />
        <span className="flex-1" />
        <span className="text-[11px] tabular-nums text-muted-foreground/60">
          {items.length}
        </span>
      </div>

      <RailReveal open={isOpen} contentClassName="flex flex-col">
        {items.length > 0 ? (
          <div className="ml-3">{items.map((it) => renderItem(it))}</div>
        ) : (
          <p className="ml-7 mr-3 py-1 text-[11px] italic text-muted-foreground/50">
            Empty section
          </p>
        )}
      </RailReveal>
    </div>
  );
}

// ─── Node leaf row (entities/objects) ─────────────────────────────────

function NodeRowAdapter({
  node,
  canvasId,
  canEdit,
  dragNamespace,
  selected,
  onActivate,
  onOpen,
  onContextMenu,
  className,
}: {
  node: Node;
  canvasId: string;
  canEdit: boolean;
  /** Namespaces the dnd-kit draggable id so duplicate rows across
   *  view modes / groups don't collide in the registry. */
  dragNamespace: string;
  selected: boolean;
  /** Row activation — modifier clicks multi-select, plain click opens. */
  onActivate: (id: string, e: React.MouseEvent, open: () => void) => void;
  onOpen: () => void;
  onContextMenu?: (e: React.MouseEvent, node: Node) => void;
  className?: string;
}) {
  const title = getNodeTitle(node);

  // Sections are dragged from `SectionFolderRow` (which carries the
  // workspace-bound files payload). When a section happens to render
  // as a leaf in type view, we deliberately skip the drag wire-up so
  // there's exactly one drag source per section.
  const category = categorizeNode(node);
  const isDraggable = canEdit && node.type !== "section";
  const draggableData: DraggableData = useMemo(
    () => ({
      kind: "canvas-node",
      payload: {
        nodeId: node.id,
        nodeType: node.type ?? "",
        category: category === "entity" ? "entity" : "object",
        sourceCanvasId: canvasId,
        currentParentSectionId: node.parentId ?? null,
        name: title,
      },
    }),
    [node.id, node.type, node.parentId, category, canvasId, title],
  );
  const draggable = useDraggable({
    id: `node-row:${dragNamespace}:${node.id}`,
    data: draggableData,
    disabled: !isDraggable,
  });

  return (
    <ExplorerRow
      ref={draggable.setNodeRef}
      title={title}
      leading={<NodeIcon node={node} />}
      selected={selected}
      onClick={(e) => onActivate(node.id, e, onOpen)}
      onContextMenu={
        onContextMenu
          ? (e) => onContextMenu(e, node)
          : (e) => e.preventDefault()
      }
      trailing={
        onContextMenu ? (
          <button
            type="button"
            aria-label="More actions"
            title="More actions"
            onClick={(e) => {
              e.stopPropagation();
              onContextMenu(e, node);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-n-10 hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-events-auto"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        ) : undefined
      }
      className={className}
      htmlProps={{
        ...draggable.attributes,
        ...draggable.listeners,
        onMouseEnter: () =>
          window.dispatchEvent(
            new CustomEvent("canvas:highlight-nodes", {
              detail: { nodeIds: [node.id] },
            }),
          ),
        onMouseLeave: () =>
          window.dispatchEvent(
            new CustomEvent("canvas:highlight-nodes", {
              detail: { nodeIds: null },
            }),
          ),
        style: draggable.isDragging
          ? { opacity: 0.4, cursor: "grabbing" }
          : undefined,
      }}
    />
  );
}

// ─── Icons ────────────────────────────────────────────────────────────

function NodeIcon({ node }: { node: Node }) {
  const d = node.data as Record<string, unknown> | undefined;
  const platform = getEntityPlatform(d);
  // Wrap every variant in the same 20x20 centering box that
  // `ExplorerFileRow.FileIcon` uses, so the title column lines up
  // across files, entities (Cohesive/Artifact and other platforms),
  // and objects within a single list.
  const inner = canvasLinkTargetId(d?.entity as never) ? (
    <Frame className="h-3.5 w-3.5 text-muted-foreground" />
  ) : platform ? (
    <IntegrationIcon platform={platform} className="h-3.5 w-3.5 rounded-sm" />
  ) : node.type === "canvasShape" ? (
    <Square className="h-3.5 w-3.5 text-teal" />
  ) : node.type === "textNote" ? (
    <StickyNote className="h-3.5 w-3.5 text-gold dark:text-gold" />
  ) : node.type === "canvasText" ? (
    <Type className="h-3.5 w-3.5 text-muted-foreground" />
  ) : node.type === "section" ? (
    <SquareDashedMousePointer className="h-3.5 w-3.5 text-foreground/70" />
  ) : node.type === "widgetNode" ? (
    <Wand2 className="h-3.5 w-3.5 text-plum" />
  ) : (
    <Box className="h-3.5 w-3.5 text-muted-foreground" />
  );
  return (
    <span className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center">
      {inner}
    </span>
  );
}

function ContentGroupIcon({ group }: { group: ContentGroup }) {
  if (group.platform) {
    return (
      <IntegrationIcon
        platform={group.platform}
        className="h-4 w-4 shrink-0 rounded-sm"
      />
    );
  }

  switch (group.key) {
    case "__files__":
      return <FileIcon className="h-4 w-4 shrink-0 text-teal" />;
    case "__shapes__":
      return <Square className="h-4 w-4 shrink-0 text-teal" />;
    case "__notes__":
      return (
        <StickyNote className="h-4 w-4 shrink-0 text-gold dark:text-gold" />
      );
    case "__labels__":
      return <Type className="h-4 w-4 shrink-0 text-muted-foreground" />;
    case "__sections__":
      return (
        <SquareDashedMousePointer className="h-4 w-4 shrink-0 text-foreground/70" />
      );
    case "__widgets__":
      return <Wand2 className="h-4 w-4 shrink-0 text-plum" />;
    default:
      return <Box className="h-4 w-4 shrink-0 text-muted-foreground" />;
  }
}
